import { Injectable, inject, signal, computed } from '@angular/core';
import { ApiService } from './api.service';
import { AuthCheckResponse } from '../models/auth-check-response.model';
import { Observable, tap } from 'rxjs';

/**
 * Wiki Permission model (simplified):
 * 
 * - /Wiki/Admin: Full access to all Wiki functions (bypasses category roles)
 * - Category ReaderRoles: Determines who can read a category and its articles
 * - Category EditorRoles: Determines who can create/edit/delete articles and manage the category
 * 
 * The actual permission checks are done server-side based on the user's roles
 * matching the category's ReaderRoles/EditorRoles.
 */
export const WikiPermissions = {
  // Admin permission - grants full access to all wiki content
  ADMIN: '/Wiki/Admin'
} as const;

export type WikiPermission = typeof WikiPermissions[keyof typeof WikiPermissions];

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  
  // Auth state signals
  private readonly _authResponse = signal<AuthCheckResponse | null>(null);
  private readonly _isLoading = signal(true);
  private readonly _isAuthenticated = signal(false);

  // Public readonly signals
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly authResponse = this._authResponse.asReadonly();
  
  readonly userId = computed(() => this._authResponse()?.userId ?? null);
  readonly userName = computed(() => {
    const response = this._authResponse();
    if (response?.firstName && response?.lastName) {
      return `${response.firstName} ${response.lastName}`;
    }
    return response?.userName || 'User';
  });
  readonly userRights = computed(() => this._authResponse()?.rights ?? []);
  readonly userRoleIds = computed(() => this._authResponse()?.roleIds ?? []);

  /**
   * Check if user is Wiki Admin (has /Wiki/Admin right or SuperAdministrator).
   * Wiki Admins have full access to all categories and articles.
   */
  readonly isWikiAdmin = computed(() => {
    const rights = this.userRights();
    return rights.includes('SuperAdministrator') || rights.includes(WikiPermissions.ADMIN);
  });

  /**
   * Check authentication status from the API.
   * This should be called once when the app initializes.
   * If already authenticated, returns the cached response.
   */
  checkAuthentication(): Observable<AuthCheckResponse> {
    // If already loaded, return cached response
    const existingResponse = this._authResponse();
    if (existingResponse && !this._isLoading()) {
      return new Observable(subscriber => {
        subscriber.next(existingResponse);
        subscriber.complete();
      });
    }
    
    this._isLoading.set(true);
    
    return this.apiService.checkAuthentication().pipe(
      tap((response: AuthCheckResponse) => {
        this._authResponse.set(response);
        this._isAuthenticated.set(response.isAuthenticated);
        this._isLoading.set(false);
      })
    );
  }

  /**
   * Check if user can read a category based on their roles.
   * User can read if they are Wiki Admin OR if any of their roles is in category's ReaderRoles or EditorRoles.
   */
  canReadCategory(categoryReaderRoleIds: number[], categoryEditorRoleIds: number[]): boolean {
    if (this.isWikiAdmin()) {
      return true;
    }
    
    const userRoles = this.userRoleIds();
    const allowedRoles = new Set([...categoryReaderRoleIds, ...categoryEditorRoleIds]);
    
    return userRoles.some(roleId => allowedRoles.has(roleId));
  }

  /**
   * Check if user can edit a category based on their roles.
   * User can edit if they are Wiki Admin OR if any of their roles is in category's EditorRoles.
   */
  canEditCategory(categoryEditorRoleIds: number[]): boolean {
    if (this.isWikiAdmin()) {
      return true;
    }
    
    const userRoles = this.userRoleIds();
    return userRoles.some(roleId => categoryEditorRoleIds.includes(roleId));
  }

  /**
   * Check if user can create root categories (only Wiki Admins).
   */
  canCreateRootCategory(): boolean {
    return this.isWikiAdmin();
  }

  /**
   * Check if user has any editorial capability.
   * Used to show/hide create buttons when no specific category is selected.
   * Returns true if user is Wiki Admin (actual category-level checks are done separately).
   */
  hasAnyEditRights(): boolean {
    return this.isWikiAdmin();
  }

  /**
   * Clear auth state (for logout scenarios).
   */
  clearAuth(): void {
    this._authResponse.set(null);
    this._isAuthenticated.set(false);
    this._isLoading.set(false);
  }
}
